"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import TitleBlock from "./TitleBlock";
import Stage, { StageSlide } from "./Stage";
import TimelineNav from "./TimelineNav";
import TextSlides, { TextSlide } from "./TextSlides";
import Images from "@/app/assets/assets";
// PERF: GSAP + ScrollTrigger are loaded lazily via ensureGsap() so they stay
// out of the initial route bundle and registerPlugin runs after mount, not at
// module evaluation. Animation behavior is unchanged.
import { ensureGsap } from "@/lib/EnsureGsap";
import Image from "next/image";

type JourneyTimelineProps = {
  className?: string;
};

const years = [2003, 2008, 2013, 2018, 2023, 2026];

// Fraction of the total scroll distance dedicated to holding still on the
// first and last slide, so they're fully visible/readable before the
// animation starts advancing and after it finishes, instead of only
// existing for a single instantaneous scroll position.
const HOLD_FRACTION = 0.1;

export default function JourneyTimeline({ className }: JourneyTimelineProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stickyRef = useRef<HTMLElement | null>(null);

  const stageSlides: StageSlide[] = useMemo(() => {
    return years.map((y, idx) => ({
      year: y,
      imageKey: `date_${y}` as StageSlide["imageKey"],
      rotationClass: idx % 2 === 0 ? "-rotate-6" : "rotate-6",
      alt: `Slide ${y}`,
    }));
  }, []);

  const yearTextMap: Record<number, string> = {
    2003: "Konstant Infosolutions was established in 2003 by Vipin Jain and Manish Jain under the name “Web Integrated”. The founders with just 2 other employees began the journey with an aim to bring revolution in the tech industry by delivering reliable solutions. During the early years, we focused on building a strong technical foundation and presence in the IT services to lay the groundwork for future growth.",

    2008: "From 2007 to 2008, we achieved growth in both our team and service capabilities. Our team grew to over 50+ professionals, and we marked our presence into services such as QA, SEO, and enterprise solutions. During this period, we also entered the European market, formed partnerships with organizations in the USA and Europe, and set-up a new office facility to support our expanding operations.",

    2013: "Between 2011 and 2012, we received notable recognition for our work and industry involvement. One of our developed platforms, Architecture Live, received the A3F Journalism Award. During this period, we became partners with Adobe and Microsoft, joined the Drupal Association, gained membership with the International Game Developers Association (IGDA), and formed strategic partnerships with Intel and Authorize.Net.",

    2018: "In 2018, we showcased our mobile applications and IT solutions at Gitex Technology Week in Dubai, strengthening our global presence. The same year, we received the Best App Development Agency award at the Tabby Awards and were ranked among the top mobile app development companies by SiliconIndia. These milestones reflected our growing international visibility and industry recognition.",

    2023: "From 2021 through 2023, we continued to receive media coverage and industry recognition from publications such as The Hindu and Hindustan Times, along with global research platforms. With over two decades of experience, we operate today as a trusted global IT and app development partner, focused on innovation, scalable solutions, and long-term client relationships.",

    2026: "From 2023 to the end of 2025, the world realized the emerging technology’s requirements in businesses, when Artificial Intelligence and Machine Learning transformed the industry landscape. We excel in the market by offering Artificial Intelligence and Machine Learning services, delivering exceptional results to our clients through continuous advancements in 2025. We have updated our website to showcase our offerings of advanced technology expertise in AI / ML, along with other emerging technologies.",
  };

  const bodyByYears = (year: number) => {
    return yearTextMap[year] ?? "";
  };
  const textSlides: TextSlide[] = useMemo(() => {
    return years.map((y) => ({
      year: y,
      title: `Milestone ${y}`,
      body: bodyByYears(y),
    }));
  }, []);

  // Geometry
  const slideGap = 420;
  const slideHeight = 350;
  const unit = slideGap + slideHeight;

  useEffect(() => {
    let cleanup: (() => void) | null = null;
    let cancelled = false;

    ensureGsap().then(({ gsap }) => {
      if (cancelled) return;

      const sticky = stickyRef.current;
      const yearTrack = document.getElementById("yearTrack");
      const imageTrack = document.getElementById("imageTrack");
      const timelineTrack = document.getElementById("timelineTrack");
      const container = containerRef.current;
      if (!sticky || !yearTrack || !imageTrack || !timelineTrack || !container)
        return;

      const total = years.length;

      const yearEls = Array.from(yearTrack.children) as HTMLElement[];
      const imgEls = Array.from(imageTrack.children) as HTMLElement[];

      const yearUnit =
        yearEls.length > 1 ? yearEls[1].offsetTop - yearEls[0].offsetTop : 0;
      const imageUnit =
        imgEls.length > 1 ? imgEls[1].offsetTop - imgEls[0].offsetTop : 0;

      const totalTravelYear = yearUnit * (total - 1);
      const totalTravelImg = imageUnit * (total - 1);

      // Set initial positions
      gsap.set(yearTrack, { y: 0 });
      gsap.set(imageTrack, { y: 0 });

      const viewportCenterX = () =>
        typeof window !== "undefined" ? window.innerWidth / 2 : 0;
      const itemWidth = 120 + 64;

      // Converts a raw 0..1 scroll-timeline progress value into a "fractional
      // slide index" (0..total-1), holding flat at 0 for the first
      // HOLD_FRACTION of scroll and flat at (total-1) for the last
      // HOLD_FRACTION, with a linear ramp between them. Everything that
      // depends on "which slide are we on" reads from this single function
      // so the tracks, nav, and parallax never drift out of sync.
      const progressToIndexPosition = (p: number) => {
        if (p <= HOLD_FRACTION) return 0;
        if (p >= 1 - HOLD_FRACTION) return total - 1;
        const local = (p - HOLD_FRACTION) / (1 - 2 * HOLD_FRACTION);
        return local * (total - 1);
      };

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container,
          start: "top top",
          end: "bottom bottom",
          scrub: 1, // Smooth scrub to help with TBT
        },
        defaults: { ease: "none" },
      });

      const setTimelineX = gsap.quickTo(timelineTrack, "x", {
        duration: 0.2,
        ease: "power2.out",
      });
      let lastIdx = -1;

      // Year track: hold at start, move through the middle, hold at end.
      tl.to(yearTrack, { y: 0, duration: HOLD_FRACTION })
        .to(yearTrack, { y: -totalTravelYear, duration: 1 - 2 * HOLD_FRACTION })
        .to(yearTrack, { y: -totalTravelYear, duration: HOLD_FRACTION });

      // Image track follows the exact same hold/move/hold timing so both
      // tracks always represent the same slide at the same scroll position.
      tl.to(imageTrack, { y: 0, duration: HOLD_FRACTION }, 0)
        .to(
          imageTrack,
          { y: -totalTravelImg, duration: 1 - 2 * HOLD_FRACTION },
          HOLD_FRACTION,
        )
        .to(
          imageTrack,
          { y: -totalTravelImg, duration: HOLD_FRACTION },
          1 - HOLD_FRACTION,
        );

      // Shared driver for index/nav/parallax - runs every frame the
      // timeline updates, using the same progressToIndexPosition mapping
      // as the track tweens above so nothing can fall out of sync.
      const slideEls = Array.from(
        imageTrack.querySelectorAll<HTMLElement>(".slide"),
      );
      const parallaxNodes = slideEls
        .map((s) => s.querySelector<HTMLElement>(".parallax-inner"))
        .filter(Boolean) as HTMLElement[];
      const perSlideOffset = 300; // px per index away; immediate next = +300px

      tl.eventCallback("onUpdate", () => {
        const pos = progressToIndexPosition(tl.progress());
        const idx = Math.round(pos);

        if (idx !== lastIdx) {
          lastIdx = idx;
          setActiveIndex(idx);
        }

        const centerX = viewportCenterX() - 200;
        const x = centerX - pos * itemWidth;
        setTimelineX(x);

        parallaxNodes.forEach((el, i) => {
          const delta = i - pos; // >0 means upcoming slide
          const y = delta > 0 ? delta * perSlideOffset : 0; // current and past sit at 0
          el.style.transform = `translateY(${y}px)`;
        });
      });

      cleanup = () => {
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [unit]);
  return (
    <div
      ref={containerRef}
      className={`sticky-container max-w-screen top-0 left-0 h-[6500px] bg-transparent ${className ?? ""}`}
    >
      <section
        ref={stickyRef}
        id="journey-sticky"
        className="w-[100vw] max-w-[calc(100vw-15px)] h-[100vh] overflow-hidden select-none bg-[#000] text-white sticky top-0"
      >
        <div className="texture-bg mix-blend-overlay  w-full h-full absolute top-0 left-0 z-0">
          <div className="absolute inset-0 z-0">
            <Image
              src={Images.texture}
              alt="texture"
              fill
              className="object-cover mix-blend-overlay opacity-60"
              sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
            />
          </div>
        </div>
        <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px] absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3" />
        <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px]  absolute left-0 bottom-0  -translate-x-1/3 translate-y-1/3" />
        <main className="relative w-[100vw] h-[100vh] overflow-hidden">
          <TitleBlock />
          <TextSlides slides={textSlides} activeIndex={activeIndex} />
          <Stage slides={stageSlides} />
          <TimelineNav
            years={years}
            activeIndex={activeIndex}
            onSelect={setActiveIndex}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-white/[0.02]"></div>
        </main>
      </section>
    </div>
  );
}
